// useEmailImport.ts
import type { BulkParticipant } from "@pulse/api/participantApi";
import { useQueryClient } from "@tanstack/react-query";
import type { ClipboardEvent } from "react";
import { useFieldContext } from "../form-context";

type User = { id: number; email: string };

const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

export function useEmailImport(
    customerId: number,
    getUsersByEmailsOptions: (args: { customerId: number; emails: string[] }) => unknown,
) {
    const queryClient = useQueryClient();
    const field = useFieldContext<BulkParticipant[]>();

    const parseEmails = (text: string): string[] => {
        // Picks emails even inside `Name <mail@x.com>`, CSVs, etc.
        const matches = text.match(EMAIL_RE) ?? [];
        // lower-case + de-dupe, but return original casing for display if you want
        const uniques = Array.from(new Set(matches.map((e) => e.trim().toLowerCase())));
        return uniques;
    };

    const resolveUsers = async (emails: string[]): Promise<User[]> => {
        if (emails.length === 0) return [];
        const opts = getUsersByEmailsOptions({ customerId, emails });
        // opts is a queryOptions object; fetch via queryClient
        const users = (await queryClient.fetchQuery(
            opts as Parameters<typeof queryClient.fetchQuery>[0],
        )) as User[];

        // Prime the cache to avoid refetches
        users?.forEach((u) => {
            queryClient.setQueryData(["user", u.id], u);
        });

        return users as User[];
    };

    const addParticipants = (toAdd: BulkParticipant[]) => {
        if (toAdd.length === 0) return;
        const current = field.state.value ?? [];
        field.setValue([...current, ...toAdd]);
    };

    const addByEmails = async (emails: string[]) => {
        if (emails.length === 0) return;

        const current = field.state.value ?? [];
        const existingExternal = new Set(
            current
                .filter((p) => p.isExternal && p.email)
                .map((p) => p.isExternal && p.email!.toLowerCase()),
        );
        const existingUsers = new Set(
            current.filter((p) => !p.isExternal && p.userId).map((p) => !p.isExternal && p.userId!),
        );

        const toLookup = emails.filter((e) => !existingExternal.has(e));
        const found = await resolveUsers(toLookup);
        const foundMap = new Map(found.map((u) => [u.email.toLowerCase(), u]));

        const next: BulkParticipant[] = [];
        for (const email of emails) {
            const u = foundMap.get(email);
            if (u) {
                if (
                    !existingUsers.has(u.id) &&
                    !next.some((p) => !p.isExternal && p.userId === u.id)
                ) {
                    next.push({ userId: u.id, isExternal: false });
                }
            } else if (
                !existingExternal.has(email) &&
                !next.some((p) => p.isExternal && p.email?.toLowerCase() === email)
            ) {
                next.push({ email, isExternal: true });
            }
        }
        addParticipants(next);
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        const text = e.clipboardData.getData("text");
        e.preventDefault();
        const emails = parseEmails(text);
        void addByEmails(emails);
    };

    const importCsvText = async (text: string) => {
        const emails = parseEmails(text);
        if (emails.length === 0) return false;
        await addByEmails(emails);
        return true;
    };

    return { handlePaste, importCsvText };
}
